import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Message } from '../../types';

interface ResponseDotsProps {
  messages: Message[];
  selectedModelsCount: number;
  modelColorAssignments: Map<string, string>;
  onDotClick: (messageId: string) => void;
  plusHumanEnabled?: boolean;
  txtEnabled?: boolean;
  userId?: string;
  onIonoffArrival?: (arrived: boolean) => void;
  onVisiblePromptChange?: (promptIndex: number) => void;
  scrollContainerRef?: React.MutableRefObject<HTMLDivElement | null>; // Reference to scroll container for viewport tracking
  /** Optional callback when any dot is clicked (for questionmark tracking) */
  onInteract?: () => void;
}

type DotState = {
  color: string | null;
  messageId: string | null;
  isIonoff: boolean;
  isHuman: boolean; // For +human responses
  modelColors: string[]; // For ionoff quadrants (up to 4 colors)
};

type IonoffResponse = {
  messageId: string;
  modelColors: string[];
};

export const ResponseDots: React.FC<ResponseDotsProps> = ({
  messages,
  selectedModelsCount,
  modelColorAssignments,
  onDotClick,
  plusHumanEnabled = false,
  txtEnabled = false,
  userId,
  onIonoffArrival,
  onVisiblePromptChange,
  scrollContainerRef,
  onInteract,
}) => {
  const DOT_SIZE = '0.75rem';
  const IONOFF_DOT_SIZE = '1.0rem'; // One notch bigger
  const ionoffDotBaseStyle: React.CSSProperties = {
    width: IONOFF_DOT_SIZE,
    height: IONOFF_DOT_SIZE,
  };

  // Wrapper for dot clicks that also triggers onInteract for questionmark tracking
  const handleDotClick = useCallback((messageId: string | null) => {
    if (messageId) {
      onDotClick(messageId);
      if (onInteract) onInteract();
    }
  }, [onDotClick, onInteract]);

  // Track when ionoff message arrives to trigger effects
  const [ionoffArrived, setIonoffArrived] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const previousIonoffRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Track which prompt group is currently visible in viewport
  const [visiblePromptIndex, setVisiblePromptIndex] = useState<number>(-1);
  
  // Track previous prompt count to detect new prompts
  const previousPromptCountRef = useRef<number>(0);
  
  // Track the first prompt ID to detect conversation switches
  const firstPromptIdRef = useRef<string | null>(null);
  
  // Flag to temporarily disable scroll tracking during auto-scroll
  const isAutoScrollingRef = useRef<boolean>(false);

  // Group messages by their parent user prompt
  // 🎯 FIX: Use userPromptMessageId for proper grouping instead of timestamp order
  // This ensures late-arriving responses (like p1 synthesis after p2 is sent) go to the right group
  const promptGroups = useMemo(() => {
    // 🎯 PHASE 2: Filter out overwritten messages (edited prompts and their responses)
    const activeMessages = messages.filter(msg => !msg.overwritten);
    
    // First pass - collect all user messages to create groups
    const userMessages = activeMessages.filter(msg => msg.role === 'user');
    const groupMap = new Map<string, {
      promptMessage: Message;
      responses: Message[];
      frozenColorAssignments: Map<string, string> | null;
      chipAssignments: Array<{chipId: number; color: string; openrouterId: string; displayedModelID: string}> | null;
    }>();
    
    for (const userMsg of userMessages) {
      const chipAssignments = (userMsg as any).chipAssignments || null;
      const frozenColors = (userMsg as any).colorAssignments 
        ? new Map(Object.entries((userMsg as any).colorAssignments) as [string, string][])
        : null;
      
      console.log('🎨 Loaded frozen assignments for prompt:', userMsg.id, { 
        hasChipAssignments: !!chipAssignments,
        hasFrozenColors: !!frozenColors 
      });
      
      groupMap.set(userMsg.id, {
        promptMessage: userMsg,
        responses: [],
        frozenColorAssignments: frozenColors,
        chipAssignments: chipAssignments,
      });
    }
    
    // 🎯 FIX: Second pass - assign responses to correct group by userPromptMessageId
    // This handles late-arriving responses (like p1 synthesis after p2 is sent)
    for (const msg of activeMessages) {
      if (msg.role === 'user') continue; // Skip user messages
      
      // Use userPromptMessageId if available, otherwise fall back to timestamp-based grouping
      const targetPromptId = (msg as any).userPromptMessageId;
      
      if (targetPromptId && groupMap.has(targetPromptId)) {
        // New behavior: Use explicit userPromptMessageId
        groupMap.get(targetPromptId)!.responses.push(msg);
      } else {
        // Legacy fallback: Find the latest user message before this response
        let latestUserMsg: Message | null = null;
        for (const userMsg of userMessages) {
          if (userMsg.timestamp <= msg.timestamp) {
            latestUserMsg = userMsg;
          } else {
            break;
          }
        }
        if (latestUserMsg && groupMap.has(latestUserMsg.id)) {
          groupMap.get(latestUserMsg.id)!.responses.push(msg);
        }
      }
    }
    
    // Convert map to array, maintaining user message order
    const groups = userMessages.map(userMsg => groupMap.get(userMsg.id)!);

    return groups;
  }, [messages]);

  // Track scroll position to determine which prompt is visible
  // ONLY runs on user-initiated scroll events, not on message changes
  useEffect(() => {
    if (!scrollContainerRef?.current || promptGroups.length === 0) return;

    const handleScroll = () => {
      // Skip scroll tracking during auto-scroll to prevent race conditions
      if (isAutoScrollingRef.current) return;
      
      const container = scrollContainerRef.current;
      if (!container) return;

      const containerTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      const viewportCenter = containerTop + containerHeight / 2;

      // Find which prompt group's messages are centered in viewport
      for (let i = promptGroups.length - 1; i >= 0; i--) {
        const promptId = promptGroups[i].promptMessage.id;
        const promptElement = document.getElementById(`message-${promptId}`);
        
        if (promptElement) {
          const elementTop = promptElement.offsetTop;
          
          // Check if this prompt group is in the visible area
          if (viewportCenter >= elementTop) {
            setVisiblePromptIndex(i);
            onVisiblePromptChange?.(i);
            break;
          }
        }
      }
    };

    const container = scrollContainerRef.current;
    container.addEventListener('scroll', handleScroll);
    
    // NO timer here - only update on actual scroll events
    // Initial index is set in the auto-focus effect below

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [scrollContainerRef, promptGroups, onVisiblePromptChange]);

  // Reset state when conversation changes (detected by first prompt ID changing)
  useEffect(() => {
    const currentFirstPromptId = promptGroups.length > 0 ? promptGroups[0].promptMessage.id : null;
    
    if (currentFirstPromptId && currentFirstPromptId !== firstPromptIdRef.current) {
      console.log('🔄 Conversation changed, resetting prompt tracking', {
        oldFirstPrompt: firstPromptIdRef.current?.substring(0, 8),
        newFirstPrompt: currentFirstPromptId.substring(0, 8),
        promptCount: promptGroups.length,
      });
      
      // Reset all tracking refs
      firstPromptIdRef.current = currentFirstPromptId;
      previousPromptCountRef.current = promptGroups.length;
      
      // Reset visible index to the latest prompt (or -1 to trigger initial set)
      setVisiblePromptIndex(promptGroups.length - 1);
      onVisiblePromptChange?.(promptGroups.length - 1);
    }
  }, [promptGroups, onVisiblePromptChange]);

  // Set initial visible index when conversation first loads (single prompt)
  useEffect(() => {
    if (promptGroups.length === 1 && visiblePromptIndex === -1) {
      setVisiblePromptIndex(0);
      onVisiblePromptChange?.(0);
    }
  }, [promptGroups.length, visiblePromptIndex, onVisiblePromptChange]);

  // Auto-scroll to new prompt when a new prompt is added (e.g., P2 sent)
  // This MUST set the visible index before any scroll tracking can interfere
  useEffect(() => {
    const currentPromptCount = promptGroups.length;
    const previousPromptCount = previousPromptCountRef.current;
    
    // Detect if a NEW prompt was added (not just initial load)
    if (currentPromptCount > previousPromptCount && previousPromptCount > 0) {
      // New prompt added - focus on it immediately
      const newPromptIndex = currentPromptCount - 1;
      const newPrompt = promptGroups[newPromptIndex];
      
      if (newPrompt) {
        // CRITICAL: Set auto-scrolling flag FIRST to block scroll tracking
        isAutoScrollingRef.current = true;
        
        // Update visible index IMMEDIATELY so dots refresh to track the new prompt
        setVisiblePromptIndex(newPromptIndex);
        onVisiblePromptChange?.(newPromptIndex);
        
        console.log('🎯 New prompt detected, switching dots to P' + (newPromptIndex + 1), {
          previousCount: previousPromptCount,
          currentCount: currentPromptCount,
          newIndex: newPromptIndex,
        });
        
        // Then handle scrolling if element exists
        if (scrollContainerRef?.current) {
          const promptElement = document.getElementById(
            `message-${newPrompt.promptMessage.id}`
          );
          if (promptElement) {
            const scrollParent = scrollContainerRef.current;
            const paddingOffset = 12;
            const targetTop = Math.max(promptElement.offsetTop - paddingOffset, 0);
            
            // Use smooth scroll for better UX
            scrollParent.scrollTo({ top: targetTop, behavior: 'smooth' });
          }
        }
        
        // Clear auto-scroll flag after scroll animation completes
        // Use a longer timeout to ensure smooth scroll fully completes
        setTimeout(() => {
          isAutoScrollingRef.current = false;
          console.log('🎯 Auto-scroll complete, scroll tracking re-enabled');
        }, 800);
      }
    }
    
    // Update ref for next comparison
    previousPromptCountRef.current = currentPromptCount;
  }, [promptGroups, scrollContainerRef, onVisiblePromptChange]);

  // Determine which prompt group is currently being processed (for dot coloring)
  // We always color dots for arrived responses, but the visible group gets full saturation
  const currentPromptGroupIndex = useMemo(() => {
    // No prompts yet
    if (promptGroups.length === 0) return -1;
    
    // OPTIMIZATION: For single-prompt conversations, always track that prompt
    // This avoids viewport tracking race conditions in fast-completing conversations
    if (promptGroups.length === 1) {
      return 0;
    }
    
    // For multi-prompt conversations: Use viewport tracking if available
    if (visiblePromptIndex >= 0 && visiblePromptIndex < promptGroups.length) {
      console.log('🎯 currentPromptGroupIndex using visiblePromptIndex:', visiblePromptIndex);
      return visiblePromptIndex;
    }
    
    // Fallback: Default to most recent prompt
    console.log('🎯 currentPromptGroupIndex fallback to latest:', promptGroups.length - 1);
    return promptGroups.length - 1;
  }, [visiblePromptIndex, promptGroups]);

  // Calculate dot states based on messages and visible prompt
  const dotStates = useMemo(() => {
    // When +human is enabled, add 1 extra dot for human response
    const humanDotCount = plusHumanEnabled ? 1 : 0;
    const totalDots = selectedModelsCount + humanDotCount + 1; // +1 for ionoff final response
    
    const dots: DotState[] = Array.from({ length: totalDots }, () => ({
      color: null,
      messageId: null,
      isIonoff: false,
      isHuman: false,
      modelColors: [],
    }));

    // Get the current prompt group we're tracking
    const currentGroup = currentPromptGroupIndex >= 0 ? promptGroups[currentPromptGroupIndex] : null;
    if (!currentGroup) return { dots, hasIonoffArrived: false };
    
    console.log('🔵 dotStates calculating for prompt group:', {
      currentPromptGroupIndex,
      promptId: currentGroup.promptMessage.id.substring(0, 8),
      responsesCount: currentGroup.responses.length,
      totalGroups: promptGroups.length,
    });

    // NEW: Chip-ID based assignments are canonical
    // LEGACY: Fall back to color map for old messages
    const chipAssignments = currentGroup.chipAssignments;
    const effectiveColorAssignments = currentGroup.frozenColorAssignments || modelColorAssignments;
    
    console.log('🎨 Using assignments for dots:', {
      hasChipAssignments: !!chipAssignments,
      hasFrozenColors: !!currentGroup.frozenColorAssignments,
      colorAssignmentCount: effectiveColorAssignments.size,
      promptMessageId: currentGroup.promptMessage.id,
    });

    // Find all model responses, human response, and the final ionoff response FOR THIS PROMPT GROUP
    const groupStartTime = currentGroup.promptMessage.timestamp;
    const modelResponses: Array<{ messageId: string; modelUsed: string; color: string }> = [];
    let humanResponseData: { messageId: string; humanUserId: string } | null = null;
    let ionoffResponse: IonoffResponse | null = null;

    console.log('🔵 ResponseDots: Processing messages', {
      totalMessages: messages.length,
      selectedModelsCount,
      totalDots,
      plusHumanEnabled,
      txtEnabled,
      modelColorAssignmentsSize: effectiveColorAssignments.size,
      modelColorAssignmentsKeys: Array.from(effectiveColorAssignments.keys()),
    });

    // Only process messages that belong to this prompt group
    for (const msg of currentGroup.responses) {
      // Human response: assistant message with isHumanResponse flag
      if (msg.role === 'assistant' && msg.isHumanResponse && msg.humanUserId) {
        humanResponseData = {
          messageId: msg.id,
          humanUserId: msg.humanUserId,
        };
        console.log('🔵 ResponseDots: Found human response', {
          messageId: msg.id.substring(0, 8),
          humanUserId: msg.humanUserId,
        });
      }

      // Model responses: assistant messages with modelUsed, not synthesized or synthesis variants
      // Exclude system messages (ionoff-debug, ionoff-error, ionoff-system) and human responses
      if (
        msg.role === 'assistant' &&
        msg.modelUsed &&
        !msg.isSynthesized &&
        !msg.synthesisVariant &&
        !msg.isGradingResponse &&
        !msg.isDebugInfo &&
        !msg.isGradeMatrix &&
        !msg.modelUsed.startsWith('ionoff-') &&
        !msg.isHumanResponse
      ) {
        const messageChipId = (msg as any)?.chipId as number | null | undefined;
        
        // NEW PATH: Use chip-ID for robust color lookup
        // Note: chipId will be null for fallback models not in original selection
        let color: string | undefined;
        if (chipAssignments && messageChipId !== undefined && messageChipId !== null) {
          // Match model to its frozen color by actualModelId (openrouterId) or modelUsed (displayedModelID)
          const chipAssignment = chipAssignments.find((c: {chipId: number; color: string; openrouterId: string; displayedModelID: string}) => {
            // First try exact match on actualModelId (openrouterId)
            if (msg.actualModelId && c.openrouterId === msg.actualModelId) {
              return true;
            }
            // Then try exact match on modelUsed (displayedModelID)
            if (msg.modelUsed && c.displayedModelID === msg.modelUsed) {
              return true;
            }
            // Fallback: strip reasoning suffix and try again
            const cleanModelUsed = msg.modelUsed?.replace(/\s*\((high|low|medium|default|reasoning)\)\s*$/, '');
            if (cleanModelUsed && c.displayedModelID === cleanModelUsed) {
              return true;
            }
            return false;
          });
          color = chipAssignment?.color;
        } else {
          // LEGACY PATH: String matching for old messages (pre chipAssignments)
          const cleanModelId = msg.modelUsed.replace(/\s*\((high|low|medium|default)\)\s*$/, '');
          color = effectiveColorAssignments.get(cleanModelId);
        }
        
        console.log('🔵 ResponseDots: Found model response', {
          messageId: msg.id.substring(0, 8),
          modelUsed: msg.modelUsed,
          chipId: messageChipId,
          hasColor: !!color,
          color,
        });
        
        if (color) {
          modelResponses.push({
            messageId: msg.id,
            modelUsed: msg.modelUsed, // Keep original for potential debug purposes
            color: color, // Store the resolved color
          });
        }
      }

      // Final ionoff response: isSynthesized but no synthesisVariant
      // Only count as "arrived" when synthesizing is complete (not still in progress)
      if (msg.isSynthesized && !msg.synthesisVariant && !msg.synthesizing) {
        // Collect colors from all model responses that arrived before this
        const colors = modelResponses.map(r => r.color);
        
        ionoffResponse = {
          messageId: msg.id,
          modelColors: colors,
        };
      }
    }
    
    // Check if ionoff has arrived for this group
    const hasIonoffArrived = ionoffResponse !== null;

    // Fill in dots based on arrival order
    // When +human is enabled, first dot is for human response
    let dotIndex = 0;
    
    // Fill human dot (if +human enabled)
    if (plusHumanEnabled && humanResponseData) {
      // Get human color from effectiveColorAssignments (userId should be in there)
      const humanColor = effectiveColorAssignments.get(humanResponseData.humanUserId);
      
      // When TxT is OFF, use hollow dot (white outline, transparent inside)
      // When TxT is ON and human response arrived, use the assigned color
      dots[dotIndex] = {
        color: txtEnabled && humanColor ? humanColor : null,
        messageId: humanResponseData.messageId,
        isIonoff: false,
        isHuman: true,
        modelColors: [],
      };
      console.log('🔵 ResponseDots: Filling human dot', {
        index: dotIndex,
        txtEnabled,
        humanColor,
        messageId: humanResponseData.messageId.substring(0, 8),
      });
      dotIndex++;
    } else if (plusHumanEnabled) {
      // Reserve first dot for human (not yet arrived)
      dotIndex++;
    }
    
    // Fill model response dots
    modelResponses.forEach((response, index) => {
      if (dotIndex < totalDots - 1) { // Leave last dot for ionoff
        // Color is already resolved via chip-ID or string matching
        console.log('🔵 ResponseDots: Filling model dot', {
          index: dotIndex,
          modelUsed: response.modelUsed,
          color: response.color,
          messageId: response.messageId.substring(0, 8),
        });
        dots[dotIndex] = {
          color: response.color || null,
          messageId: response.messageId,
          isIonoff: false,
          isHuman: false,
          modelColors: [],
        };
        dotIndex++;
      }
    });

    console.log('🔵 ResponseDots: Final dot states', {
      totalDots,
      filledDots: dots.filter(d => d.color || d.isIonoff).length,
      dots: dots.map((d, i) => ({
        index: i,
        hasColor: !!d.color,
        color: d.color,
        isIonoff: d.isIonoff,
        hasMessage: !!d.messageId,
      })),
    });

    // Fill in ionoff dot (always last)
    if (ionoffResponse !== null) {
      const response: IonoffResponse = ionoffResponse;
      dots[totalDots - 1] = {
        color: null, // Ionoff uses quadrants, not solid color
        messageId: response.messageId,
        isIonoff: true,
        isHuman: false,
        modelColors: response.modelColors,
      };
    }

    return { dots, hasIonoffArrived };
  }, [messages, selectedModelsCount, modelColorAssignments, plusHumanEnabled, txtEnabled, currentPromptGroupIndex, promptGroups]);

  // Detect when ionoff message arrives and trigger effects
  // Only trigger when synthesis is COMPLETE (not still in progress)
  useEffect(() => {
    const ionoffMessage = messages.find(m => m.isSynthesized && !m.synthesisVariant && !m.synthesizing);
    
    if (ionoffMessage && ionoffMessage.id !== previousIonoffRef.current) {
      // New ionoff message arrived
      const isNewMessage = previousIonoffRef.current === null; // First load vs new message
      previousIonoffRef.current = ionoffMessage.id;
      setIonoffArrived(true);
      
      // Only play sound and animate if this is a NEW message (not on conversation load)
      if (isNewMessage) {
        setShowAnimation(true);
        
        // Notify parent component
        if (onIonoffArrival) {
          onIonoffArrival(true);
        }
        
        // Play notification sound (iMessage-like tone)
        // Using a simple beep tone via Web Audio API
        try {
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = 800; // Hz
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.3);
        } catch (error) {
          console.warn('Could not play notification sound:', error);
        }
        
        // Remove animation class after it completes
        setTimeout(() => {
          setShowAnimation(false);
        }, 1000);
      }
    }
  }, [messages, onIonoffArrival]);

  return (
    <div className="flex items-center justify-center">
      {/* Response dots */}
      <div className={`flex items-center gap-3 ${showAnimation ? 'animate-slide-in' : ''}`}>
        {dotStates.dots.map((dot, index) => {
        // For human dots with special styling
        if (dot.isHuman) {
          // When TxT is OFF: hollow dot (outline only)
          // When TxT is ON and color assigned: filled with color
          if (!txtEnabled || !dot.color) {
            return (
              <button
                key={index}
                onClick={() => handleDotClick(dot.messageId)}
                disabled={!dot.messageId}
                className={`
                  rounded-full transition-all
                  ${dot.messageId ? 'cursor-pointer hover:scale-125' : 'cursor-default'}
                  border-2
                `}
                style={{
                  width: DOT_SIZE,
                  height: DOT_SIZE,
                  borderColor: 'currentColor',
                  backgroundColor: 'transparent',
                }}
                title={dot.messageId ? 'Your response' : 'Waiting for your response...'}
              />
            );
          }
          
          // TxT is ON and color is assigned: filled dot
          return (
            <button
              key={index}
              onClick={() => handleDotClick(dot.messageId)}
              disabled={!dot.messageId}
              className={`
                rounded-full transition-all
                ${dot.messageId ? 'cursor-pointer hover:scale-125' : 'cursor-default'}
              `}
              style={{
                width: DOT_SIZE,
                height: DOT_SIZE,
                backgroundColor: dot.color,
              }}
              title="Your response"
            />
          );
        }
        
        // For ionoff dots with multiple colors, render divisions based on count
        if (dot.isIonoff && dot.modelColors.length > 0) {
          const colors = dot.modelColors;
          const colorCount = colors.length;
          
          // 1 color: solid
          if (colorCount === 1) {
            return (
              <button
                key={index}
                onClick={() => handleDotClick(dot.messageId)}
                disabled={!dot.messageId}
                className={`
                  rounded-full transition-all
                  ${dot.messageId ? 'cursor-pointer hover:scale-125' : 'cursor-default'}
                `}
                style={{
                  ...ionoffDotBaseStyle,
                  backgroundColor: colors[0],
                }}
                title="Final ionoff response"
              />
            );
          }
          
          // 2 colors: split in half (left/right)
          if (colorCount === 2) {
            return (
              <button
                key={index}
                onClick={() => handleDotClick(dot.messageId)}
                disabled={!dot.messageId}
                className={`
                  relative rounded-full transition-all overflow-hidden
                  ${dot.messageId ? 'cursor-pointer hover:scale-125' : 'cursor-default'}
                `}
                style={{
                  ...ionoffDotBaseStyle,
                }}
                title="Final ionoff response"
              >
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: `conic-gradient(${colors[0]} 0deg 180deg, ${colors[1]} 180deg 360deg)`,
                  }}
                />
              </button>
            );
          }
 
          // 3 colors: divide into equal pie thirds
          if (colorCount === 3) {
            return (
              <button
                key={index}
                onClick={() => handleDotClick(dot.messageId)}
                disabled={!dot.messageId}
                className={`
                  relative rounded-full transition-all overflow-hidden
                  ${dot.messageId ? 'cursor-pointer hover:scale-125' : 'cursor-default'}
                `}
                style={{
                  ...ionoffDotBaseStyle,
                }}
                title="Final ionoff response"
              >
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: `conic-gradient(${colors[0]} 0deg 120deg, ${colors[1]} 120deg 240deg, ${colors[2]} 240deg 360deg)`,
                  }}
                />
              </button>
            );
          }
 
          // 4+ colors: quadrants (pie quarters)
          const quadrantColors = colors.slice(0, 4);
          return (
            <button
              key={index}
              onClick={() => handleDotClick(dot.messageId)}
              disabled={!dot.messageId}
              className={`
                relative rounded-full transition-all overflow-hidden
                ${dot.messageId ? 'cursor-pointer hover:scale-125' : 'cursor-default'}
              `}
              style={{
                ...ionoffDotBaseStyle,
              }}
              title="Final ionoff response"
            >
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: `conic-gradient(${quadrantColors[0]} 0deg 90deg, ${quadrantColors[1]} 90deg 180deg, ${quadrantColors[2]} 180deg 270deg, ${quadrantColors[3] || quadrantColors[0]} 270deg 360deg)`,
                }}
              />
            </button>
          );
        }
        
        // Regular solid color dots
        return (
          <button
            key={index}
            onClick={() => handleDotClick(dot.messageId)}
            disabled={!dot.messageId}
            className={`
              rounded-full transition-all
              ${dot.messageId ? 'cursor-pointer hover:scale-125' : 'cursor-default'}
              ${!dot.color && !dot.isIonoff ? 'bg-gray-400' : ''}
            `}
            style={{
              width: DOT_SIZE,
              height: DOT_SIZE,
              backgroundColor: dot.color || undefined,
              opacity: dot.color && dotStates.hasIonoffArrived ? 0.35 : 1,
            }}
            title={
              dot.messageId
                ? 'Model response'
                : 'Waiting for response...'
            }
          />
        );
      })}
      </div>
    </div>
  );
};

